import produce from 'immer';
import moment from 'moment';
import { campaign_types } from '../../models/campaign-shared';
import { cartStore } from './store';

export type CartItemType =
    | 'recurrent'
    | 'onetime' // For addons that have a one time price
    | 'initial' // Aka 'oprettelse'
    | 'shipment'
    | 'sum'
    | 'info';

export interface IItemWithPrice {
    emphasized?: boolean;
    groupIndex?: number;
    title: string;
    description?: string;
    price: number;
    beforePrice?: number;
    noOfCampaignMonths?: number;
    priceUnit?: string;
    type: CartItemType;
    removeFn?: () => void;
}

export interface ICartItem extends IItemWithPrice {
    offerText?: string;
}

export interface ICartSummary {
    summaryLines: IItemWithPrice[];
    total: IItemWithPrice;
    offer: IItemWithPrice;
}

export type CartSectionType = 'Broadband' | 'TVPackage' | 'TVVaelgSelv';

export interface ICartSection {
    campaign?: campaign_types;
    title: string;
    items: ICartItem[];
    type: CartSectionType;
    itemDataBag?: any;
    addFn?: () => void;
    addFnButtonText?: string;
    removeFn?: () => void;
}

export interface ICartModel {
    sections: ICartSection[];
    summary: ICartSummary;
}

export const CART_LOCALSTORAGE_KEY = 'Stofa Cart Content';

export interface ICartLocalStorageItem {
    cartSections: ICartSection[];
    ttlMinutes?: number;
    lastUpdated?: Date;
}

export function setStoredCart(cart: ICartLocalStorageItem): void {
    if (!window.localStorage) {
        throw new Error('localStorage is not available');
    }

    const preparedCart = produce(cart, (draft) => {
        draft.lastUpdated = moment().toDate();
        draft.ttlMinutes = moment.duration(1, 'day').asMinutes();
    });

    window.localStorage.setItem(
        CART_LOCALSTORAGE_KEY,
        JSON.stringify(preparedCart)
    );
}

export function getStoredCart(): ICartLocalStorageItem | null {
    if (!window.localStorage) {
        throw new Error('localStorage is not available');
    }

    const serializedCart = window.localStorage.getItem(CART_LOCALSTORAGE_KEY);

    if (serializedCart) {
        const cart: ICartLocalStorageItem = JSON.parse(serializedCart);
        const expiryDate = moment(cart.lastUpdated)
            .add(cart.ttlMinutes, 'minutes')
            .toDate();
        if (moment().isAfter(expiryDate)) {
            // Remove if expired
            window.localStorage.removeItem(CART_LOCALSTORAGE_KEY);
            return null;
        } else {
            return cart;
        }
    } else {
        return null;
    }
}

window.addEventListener('Cart.removeTVPackage', (e) => {
    cartStore.removeTVPackage();
});

window.addEventListener('Cart.removeVaelgSelv', (e) => {
    cartStore.removeVaelgSelv();
});

window.addEventListener('Cart.removeBroadband', (e) => {
    cartStore.removeBroadband();
});

window.addEventListener('Cart.removeSuperWifi', (e) => {
    cartStore.removeSuperWifi();
});

window.addEventListener('Cart.addTVBoks', (e) => {
    cartStore.addTVBoks().then(() => console.log('TV boks added'));
});

window.addEventListener('Cart.removeTVBoks', (e) => {
    cartStore.removeTVBoks().then(() => console.log('TV boks removed'));
});

window.addEventListener('Cart.addAppleTV', (e) => {
    cartStore.addTVDevices({ appleTV: 1 });
});

window.addEventListener('Cart.addChromecast', (e) => {
    cartStore.addTVDevices({ chromecast: 1 });
});

window.addEventListener('Cart.addChromecastWithGoogleTV', (e) => {
    cartStore.addTVDevices({ chromecastWithGoogleTV: 1 });
});

window.addEventListener('Cart.removeAppleTV', (e) => {
    cartStore.removeAppleTV();
});

window.addEventListener('Cart.removeChromecast', (e) => {
    cartStore.removeChromecast();
});

window.addEventListener('Cart.removeChromecastWithGoogleTV', (e) => {
    cartStore.removeChromecastWithGoogleTV();
});

window.addEventListener('Cart.removeStreamingCampaignAddons', (e) => {
    cartStore
        .removeStreamingCampaignAddons()
        .then(() => console.log('Streaming campaign addons removed'));
});
